import { useCallback, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useAuthStore } from '@/lib/auth/authStore';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { useAiAssistantStore, type ChatMessage } from './aiAssistantStore';
import type { AssistantContext } from './useAIContext';

const BASE_URL: string = import.meta.env.VITE_API_URL ?? '';

interface StreamArgs {
  ctx: AssistantContext;
  userMessage?: string;
}

/**
 * Subscribes to the /ai/assistant/stream SSE endpoint. The hook owns the
 * AbortController for the active fetch so that closing the sidebar or
 * unmounting cancels the request (and stops Anthropic billing).
 */
export function useAIStream() {
  const abortRef = useRef<AbortController | null>(null);
  const store = useAiAssistantStore;

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  useEffect(() => () => cancel(), [cancel]);

  const send = useCallback(
    async ({ ctx, userMessage }: StreamArgs) => {
      cancel();
      const controller = new AbortController();
      abortRef.current = controller;

      const state = store.getState();
      const history: Array<{ role: 'user' | 'assistant'; content: string }> =
        state.messages
          .filter((m: ChatMessage) => !m.pending && m.content.length > 0)
          .map((m: ChatMessage) => ({ role: m.role, content: m.content }));

      const assistantId = `a-${Date.now()}`;
      if (userMessage) state.appendUser(userMessage);
      state.startAssistant(assistantId);
      state.setStreaming(true);

      const token = useAuthStore.getState().token;
      // Locks the assistant's reply language to the UI preference instead of
      // letting Claude guess from the latest message. Without this, the
      // assistant drifts to Spanish on Spanish UIs even after the user typed
      // one English word (and vice versa).
      const language = usePrefsStore.getState().language;

      try {
        const res = await fetch(`${BASE_URL}/ai/assistant/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Accept: 'text/event-stream',
          },
          body: JSON.stringify({
            contextKind: ctx.kind,
            contextRefs: ctx.refs,
            history,
            userMessage,
            language,
          }),
          signal: controller.signal,
        });

        if (!res.ok || !res.body) {
          throw new Error(`HTTP ${res.status}`);
        }

        await consumeSse(res.body, {
          onToken: (delta) => state.appendAssistantDelta(assistantId, delta),
          onChips: (chips) => state.finalizeAssistant(assistantId, chips),
          onError: (msg) => {
            // Backend reports failure over SSE (HTTP 200 + event:error). The
            // outer catch never fires, so Sentry needs an explicit capture.
            Sentry.captureMessage(
              `ai-assistant SSE error: ${msg}`,
              'error',
            );
            state.setError(msg);
          },
        });
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return;
        // Surface to Sentry — the user-facing toast doesn't tell us what failed.
        Sentry.withScope((scope) => {
          scope.setTag('module', 'ai-assistant');
          scope.setTag('contextKind', ctx.kind);
          scope.setExtras({
            contextKind: ctx.kind,
            contextRefs: ctx.refs,
            historyLength: history.length,
            hasUserMessage: Boolean(userMessage),
          });
          Sentry.captureException(err);
        });
        state.setError('assistant_unavailable');
      } finally {
        state.setStreaming(false);
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [cancel, store],
  );

  return { send, cancel };
}

interface SseHandlers {
  onToken: (delta: string) => void;
  onChips: (chips: string[]) => void;
  onError: (message: string) => void;
}

/**
 * Minimal SSE parser. The backend writes blocks of the form:
 *   event: <name>\n
 *   data: <json>\n\n
 *
 * We split on the blank line (`\n\n`) and parse each block, holding any
 * partial trailing block until the next chunk arrives.
 */
async function consumeSse(
  body: ReadableStream<Uint8Array>,
  handlers: SseHandlers,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buf = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buf.indexOf('\n\n')) !== -1) {
      const block = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      dispatchBlock(block, handlers);
    }
  }
  if (buf.trim()) dispatchBlock(buf, handlers);
}

function dispatchBlock(block: string, handlers: SseHandlers): void {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return;
  let payload: unknown;
  try {
    payload = JSON.parse(dataLines.join('\n'));
  } catch {
    return;
  }
  switch (event) {
    case 'token':
      if (
        payload &&
        typeof (payload as { delta?: unknown }).delta === 'string'
      ) {
        handlers.onToken((payload as { delta: string }).delta);
      }
      return;
    case 'chips':
      if (Array.isArray((payload as { chips?: unknown }).chips)) {
        const chips = (payload as { chips: unknown[] }).chips
          .filter((c) => typeof c === 'string')
          .slice(0, 4) as string[];
        handlers.onChips(chips);
      }
      return;
    case 'error':
      handlers.onError(
        typeof (payload as { message?: unknown }).message === 'string'
          ? (payload as { message: string }).message
          : 'assistant_unavailable',
      );
      return;
  }
}
